/**
 * Blademaster ability clips (dual swords, red trails). Owned by the blademaster animation work.
 * Mortal Strike below is the fully commented reference example of the clip format, and
 * Bladestorm is the looping channel example. Abilities not registered here play the class
 * defaults (anim/defaults.ts).
 */
import { ABILITY } from '@keystone/content';
import { defineClip } from '../clips';
import { registerAbilityClips } from '../registry';
import { registerBlademasterKit } from '../kits/blademaster';

const RED = '#ff3b2f';
const RED_TAIL = '#6e0606';

/**
 * Mortal Strike: crouch and coil, then a full clockwise spin with both blades out, ending in a
 * two-handed overhead chop. Short freeze on impact, then a recovery.
 */
export const mortalStrike = defineClip({
  id: 'blademaster.mortal_strike',
  // Seconds. Keep strikes short (0.5–0.8 s): the GCD is 1.5 s and the next press crossfades in.
  duration: 0.78,
  // Crossfade from whatever was playing. Keep it tiny so the press reads instantly.
  blendIn: 0.05,
  // 'auto': upper body only while running, full body (legs, lunge, hop) when standing still.
  mask: 'auto',
  keys: [
    // Anticipation: coil right, crouch, swords drawn back low. 'out' = fast into the coil, soft settle.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        // [pitch fwd+, twist left+, lean right+]
        spine: [0.12, -0.55, 0],
        chest: [0.05, -0.2, 0],
        head: [0, 0.45, 0], // keep the eyes on the target while the torso coils away
        // [raise forward+, sweep across the front+, raise sideways+]
        rightUpperArm: [-0.35, -0.2, 0.55],
        leftUpperArm: [0.6, 0.6, 0.35],
        // [elbow bend+]
        rightLowerArm: [0.9],
        leftLowerArm: [1.0],
        // [thigh forward+, twist, spread+] / [knee bend+]
        leftUpperLeg: [0.35, 0, 0.12],
        rightUpperLeg: [-0.1, 0, 0.12],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.55],
      },
      // Visual-only root motion: sink a little and shift the weight back.
      root: { hop: -0.12, lunge: -0.08 },
    },
    // The spin: arms flung out sideways so the blades sweep a wide circle. -1 turn = one full
    // clockwise turn (seen from above). 'in' accelerates into the spin.
    {
      t: 0.3,
      ease: 'in',
      pose: {
        spine: [0.05, 0.3, 0],
        rightUpperArm: [0.4, 0.2, 1.15],
        leftUpperArm: [0.4, 0.2, 1.15],
        rightLowerArm: [0.15],
        leftLowerArm: [0.15],
        leftUpperLeg: [0.2, 0, 0.1],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.3],
      },
      root: { spin: -0.8, hop: 0.12, lunge: 0.15 },
    },
    // Impact: both blades chop down in front. 'back' overshoots a little (the follow-through),
    // `hold` freezes the pose for 0.07 s.
    {
      t: 0.38,
      ease: 'back',
      overshoot: 1.2,
      hold: 0.07,
      pose: {
        spine: [0.38, 0.1, 0],
        chest: [0.1, 0, 0],
        head: [-0.15, 0, 0],
        rightUpperArm: [1.05, 0.75, 0.15],
        leftUpperArm: [1.05, 0.75, 0.15],
        rightLowerArm: [0.05],
        leftLowerArm: [0.05],
        leftUpperLeg: [0.55, 0, 0.1],
        rightUpperLeg: [-0.3, 0, 0.1],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.45],
      },
      // Spin ends on a whole turn (-1 ≡ 0): blending out never unwinds it.
      root: { spin: -1, lunge: 0.35, hop: -0.05 },
    },
    // Recovery: back to the base pose. 'out' = quick at first, then settles. The lunge returns
    // to the sim position here. Keep spin at -1: keys interpolate raw values, so going back to 0
    // would turn the hero a full circle backwards during the recovery.
    { t: 0.78, ease: 'out', root: { spin: -1 } },
  ],
  // Ribbons from the real blade tips. Windows are [from, to) in clip seconds.
  trails: [
    { from: 0.12, to: 0.44, hand: 'both', color: RED, tail: RED_TAIL, style: 'crescent', width: 0.9, fade: 0.22, alpha: 0.75 },
  ],
  // Effects at moments: a slash crescent at each blade tip on impact, plus a small ground shockwave.
  cues: [
    { t: 0.38, fx: 'slash', at: 'tip', hand: 'right', scale: 1.1 },
    { t: 0.38, fx: 'slash', at: 'tip', hand: 'left', scale: 0.9 },
    { t: 0.39, fx: 'shockwave', at: 'ground', scale: 0.7 },
  ],
  // Hit-stop on impact (local player only) with a light shake.
  hitStop: { t: 0.38, ms: 55, shake: 0.18 },
});

/** Bladestorm: whirl with both blades out for the whole channel (loops until the channel ends). */
export const bladestorm = defineClip({
  id: 'blademaster.bladestorm',
  duration: 0.63,
  loop: true,
  // 0..0.18 is the intro (arms out, crouch); 0.18..0.63 repeats.
  loopFrom: 0.18,
  blendIn: 0.08,
  // The legs stay in the stance even while moving.
  mask: 'full',
  keys: [
    {
      t: 0.18,
      ease: 'out',
      pose: {
        spine: [0.2, 0, 0],
        head: [-0.1, 0, 0],
        rightUpperArm: [0.35, 0.1, 1.2],
        leftUpperArm: [0.35, 0.1, 1.2],
        rightLowerArm: [0.1],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.3, 0, 0.25],
        rightUpperLeg: [0.3, 0, 0.25],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.1 },
    },
    // Half a turn with a small bob, then the other half. Linear keeps the spin rate constant.
    {
      t: 0.405,
      ease: 'linear',
      pose: {
        spine: [0.2, 0, 0.06],
        head: [-0.1, 0, 0],
        rightUpperArm: [0.3, 0.1, 1.25],
        leftUpperArm: [0.3, 0.1, 1.25],
        rightLowerArm: [0.1],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.3, 0, 0.25],
        rightUpperLeg: [0.3, 0, 0.25],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.02, spin: -0.5 },
    },
    {
      t: 0.63,
      ease: 'linear',
      pose: {
        spine: [0.2, 0, 0],
        head: [-0.1, 0, 0],
        rightUpperArm: [0.35, 0.1, 1.2],
        leftUpperArm: [0.35, 0.1, 1.2],
        rightLowerArm: [0.1],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.3, 0, 0.25],
        rightUpperLeg: [0.3, 0, 0.25],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.1, spin: -1 },
    },
  ],
  // One window spanning the loop: the ribbons stay continuous across repeats.
  trails: [{ from: 0.1, to: 0.63, hand: 'both', color: RED, tail: RED_TAIL, style: 'crescent', width: 0.85, fade: 0.16, alpha: 0.6 }],
  // Cues inside the loop fire on every repeat.
  cues: [
    { t: 0.405, fx: 'sparks', at: 'tip', hand: 'right', scale: 0.5 },
    { t: 0.63, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
});

export function registerBlademasterClips(): void {
  registerAbilityClips(ABILITY.blademaster.mortalStrike, { strike: mortalStrike });
  registerAbilityClips(ABILITY.blademaster.bladestorm, { channel: bladestorm });
  registerBlademasterKit(); // the full Blademaster kit (anim/kits/blademaster.ts) overrides the reference clips
}
